import type {
  PartialSurfaceTokenInput,
  SurfaceToken,
  SurfaceTokenInput,
} from "./token.surface";
import { makeSurfaceToken } from "./token.surface";
import type { OverridenTokenCSSProperties } from "./util.make-generic-token";
import { makeGenericToken } from "./util.make-generic-token";

export type InteractiveSurfaceTokenInput = {
  default: SurfaceToken | SurfaceTokenInput;
  hover: SurfaceToken | SurfaceTokenInput;
  active: SurfaceToken | SurfaceTokenInput;
};

export type PartialInteractiveSurfaceTokenInput = Partial<{
  default: SurfaceToken | PartialSurfaceTokenInput;
  hover: SurfaceToken | PartialSurfaceTokenInput;
  active: SurfaceToken | PartialSurfaceTokenInput;
}>;

export type InteractiveSurfaceToken = {
  (options?: {
    state?: keyof InteractiveSurfaceTokenInput;
    priority?: "important";
    property?: keyof SurfaceTokenInput;
    alpha?: number;
  }): string;

  __override: (
    input:
      | PartialInteractiveSurfaceTokenInput
      | InteractiveSurfaceToken
      | undefined,
  ) => OverridenTokenCSSProperties;

  __extend(propName: `--${string}`): InteractiveSurfaceToken;

  default: SurfaceToken;
  hover: SurfaceToken;
  active: SurfaceToken;
};

export function makeInteractiveSurfaceToken(
  propName: `--${string}`,
  input: InteractiveSurfaceTokenInput | InteractiveSurfaceToken,
): InteractiveSurfaceToken {
  const tokens = {
    default: makeSurfaceToken(`${propName}--default`, input.default),
    hover: makeSurfaceToken(`${propName}--hover`, input.hover),
    active: makeSurfaceToken(`${propName}--active`, input.active),
  };

  return makeGenericToken(
    function format(options) {
      switch (options?.state ?? "default") {
        case "default":
          return tokens.default(options);

        case "hover":
          return tokens.hover(options);

        case "active":
          return tokens.active(options);
      }
    },

    function override(
      nextValue:
        | PartialInteractiveSurfaceTokenInput
        | InteractiveSurfaceToken
        | undefined,
    ) {
      if (nextValue === undefined) {
        return {};
      }

      return {
        ...tokens.default.__override(nextValue.default),
        ...tokens.hover.__override(nextValue.hover),
        ...tokens.active.__override(nextValue.active),
      };
    },

    function extend(propName, token) {
      return makeInteractiveSurfaceToken(propName, token);
    },

    tokens,
  );
}
